import { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Check, Mic, MicOff, UserX, Video, VideoOff, X } from 'lucide-react';
import { AvatarCircle, Button, cn } from '@jeraisonne/ui';
import type { VisioParticipant } from '../video/video-service';
import type { WaitingParticipant } from '../participants/use-waiting-participants';
import { translateAdmitError } from '../participants/admit-error';

/**
 * Panneau « Participants » — liste de la salle + actions de l'hôte (couper un micro, exclure,
 * admettre/refuser la salle d'attente). Non-hôte : liste en lecture seule (utile pour voir qui
 * est là, qui a le micro/la caméra ouverte — jamais la salle d'attente, `waiting` est toujours
 * vide côté non-hôte, cf. `useWaitingParticipants`). L'exclusion écrit en base
 * (`remove_visio_participant`, RPC — seul rempart qui empêche un retour) ET notifie tout de suite
 * via le service (`removeParticipant`, ferme la connexion en cours) : sans ce second appel,
 * l'exclu resterait visible/audible jusqu'à ce qu'il reparte de lui-même. `db` est INJECTÉ (pas de
 * singleton), comme le reste du package.
 *
 * `waiting`/`onAdmit`/`onDeny` sont optionnels (défaut : aucune section, comportement historique
 * inchangé) — branchés par `RoomPage` via `useWaitingParticipants` (Phase D, migration 0259).
 */
export function ParticipantsPanel({
  participants,
  myUserId,
  myDisplayName,
  myMicOn,
  myCameraOn,
  isHost,
  roomId,
  db,
  waiting = [],
  onClose,
  onRequestMute,
  onExcluded,
  onAdmit,
  onDeny,
}: {
  participants: VisioParticipant[];
  myUserId: string;
  myDisplayName: string;
  myMicOn: boolean;
  myCameraOn: boolean;
  isHost: boolean;
  roomId: string;
  db: SupabaseClient;
  /** Participants en salle d'attente (`status='waiting'`) — host-only, cf. `useWaitingParticipants`. */
  waiting?: WaitingParticipant[];
  onClose: () => void;
  /** Signal WebRTC — coupe le micro du participant, sans attendre un aller-retour serveur. */
  onRequestMute: (userId: string) => void;
  /** Signal WebRTC — ferme la connexion locale de l'exclu tout de suite (l'écriture DB est faite ici). */
  onExcluded: (userId: string) => void;
  /** RPC `admit_visio_participant` (via `useWaitingParticipants`). */
  onAdmit?: (userId: string) => Promise<void>;
  /** RPC `deny_visio_participant` (via `useWaitingParticipants`). */
  onDeny?: (userId: string) => Promise<void>;
}) {
  const [excluding, setExcluding] = useState<string | null>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleExclude(participant: VisioParticipant) {
    const name = participant.displayName || 'ce participant';
    if (!window.confirm(`Exclure ${name} de la salle ? Il ne pourra pas revenir.`)) return;
    setExcluding(participant.userId);
    setError(null);
    try {
      const { error: rpcError } = await db.rpc('remove_visio_participant', {
        p_room_id: roomId,
        p_user_id: participant.userId,
      });
      if (rpcError) throw new Error(rpcError.message);
      onExcluded(participant.userId);
    } catch (err) {
      console.error('[participants] exclusion échouée', err);
      setError(`Impossible d'exclure ${name}. Réessaie.`);
    } finally {
      setExcluding(null);
    }
  }

  async function handleAdmit(w: WaitingParticipant) {
    if (!onAdmit) return;
    setDecidingId(w.userId);
    setError(null);
    try {
      await onAdmit(w.userId);
    } catch (err) {
      console.error('[participants] admission échouée', err);
      setError(translateAdmitError(err instanceof Error ? err.message : '', w.displayName));
    } finally {
      setDecidingId(null);
    }
  }

  async function handleDeny(w: WaitingParticipant) {
    if (!onDeny) return;
    if (!window.confirm(`Refuser ${w.displayName || 'ce participant'} ? Il ne pourra pas revenir.`))
      return;
    setDecidingId(w.userId);
    setError(null);
    try {
      await onDeny(w.userId);
    } catch (err) {
      console.error('[participants] refus échoué', err);
      setError(`Impossible de refuser ${w.displayName || 'ce participant'}. Réessaie.`);
    } finally {
      setDecidingId(null);
    }
  }

  const rows: Array<{
    userId: string;
    displayName: string;
    micOn: boolean;
    cameraOn: boolean;
    isSelf: boolean;
    isRowHost: boolean;
  }> = [
    {
      userId: myUserId,
      displayName: `${myDisplayName || 'Toi'} (toi)`,
      micOn: myMicOn,
      cameraOn: myCameraOn,
      isSelf: true,
      isRowHost: isHost,
    },
    ...participants.map((p) => ({
      userId: p.userId,
      displayName: p.displayName || 'Participant',
      micOn: p.micOn,
      cameraOn: p.cameraOn,
      isSelf: false,
      isRowHost: p.isHost,
    })),
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="participants-panel-title"
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[80dvh] w-full max-w-md space-y-3 overflow-y-auto rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:rounded-2xl"
      >
        <div className="flex items-center justify-between">
          <p id="participants-panel-title" className="text-ink-900 text-base font-bold">
            Participants · {rows.length}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le panneau des participants"
            className="text-ink-500 flex h-11 w-11 items-center justify-center rounded-full active:scale-95"
          >
            <X className="size-5" />
          </button>
        </div>

        {error && (
          <p role="alert" className="text-accent-700 text-xs font-medium">
            {error}
          </p>
        )}

        {isHost && waiting.length > 0 && (
          <div className="border-primary-200 bg-primary-50 space-y-2 rounded-xl border p-3">
            <p className="text-primary-800 text-sm font-bold">En attente · {waiting.length}</p>
            <ul className="space-y-2">
              {waiting.map((w) => (
                <li key={w.userId} className="flex items-center gap-3">
                  <AvatarCircle name={w.displayName} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-ink-900 truncate text-sm font-medium">
                      {w.displayName}
                      {w.role === 'guest' && (
                        <span className="text-ink-400 ml-1.5 text-xs font-semibold">· Invité</span>
                      )}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={() => void handleAdmit(w)}
                      disabled={decidingId === w.userId}
                      aria-label={`Admettre ${w.displayName}`}
                      title="Admettre"
                      className="bg-primary-600 hover:bg-primary-700 flex size-9 items-center justify-center rounded-full text-white transition-colors active:scale-95 disabled:opacity-50"
                    >
                      <Check className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDeny(w)}
                      disabled={decidingId === w.userId}
                      aria-label={`Refuser ${w.displayName}`}
                      title="Refuser"
                      className="text-accent-600 hover:bg-accent-50 flex size-9 items-center justify-center rounded-full transition-colors active:scale-95 disabled:opacity-50"
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul className="divide-ink-100 divide-y">
          {rows.map((row) => (
            <li key={row.userId} className="flex items-center gap-3 py-2">
              <AvatarCircle name={row.displayName} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="text-ink-900 truncate text-sm font-medium">
                  {row.displayName}
                  {row.isRowHost && (
                    <span className="text-primary-600 ml-1.5 text-xs font-semibold">· Hôte</span>
                  )}
                </p>
              </div>
              <span
                className={cn(
                  'flex size-6 items-center justify-center',
                  !row.micOn && 'text-accent-600',
                )}
                aria-label={
                  row.micOn
                    ? `Micro ouvert : ${row.displayName}`
                    : `Micro coupé : ${row.displayName}`
                }
              >
                {row.micOn ? <Mic className="size-4" /> : <MicOff className="size-4" />}
              </span>
              <span
                className={cn(
                  'flex size-6 items-center justify-center',
                  !row.cameraOn && 'text-ink-400',
                )}
                aria-label={
                  row.cameraOn
                    ? `Caméra allumée : ${row.displayName}`
                    : `Caméra éteinte : ${row.displayName}`
                }
              >
                {row.cameraOn ? <Video className="size-4" /> : <VideoOff className="size-4" />}
              </span>
              {isHost && !row.isSelf && !row.isRowHost && (
                <div className="flex shrink-0 gap-1">
                  {row.micOn && (
                    <button
                      type="button"
                      onClick={() => onRequestMute(row.userId)}
                      aria-label={`Couper le micro de ${row.displayName}`}
                      title="Couper le micro"
                      className="text-ink-500 hover:bg-ink-100 flex size-9 items-center justify-center rounded-full transition-colors active:scale-95"
                    >
                      <MicOff className="size-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      void handleExclude(participants.find((p) => p.userId === row.userId)!)
                    }
                    disabled={excluding === row.userId}
                    aria-label={`Exclure ${row.displayName}`}
                    title="Exclure"
                    className="text-accent-600 hover:bg-accent-50 flex size-9 items-center justify-center rounded-full transition-colors active:scale-95 disabled:opacity-50"
                  >
                    <UserX className="size-4" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>

        <Button onClick={onClose} className="min-h-[2.75rem] w-full">
          Fermer
        </Button>
      </div>
    </div>
  );
}
